import { useState } from 'react'

import { addQuantIndicator, newQuantIndicatorId, removeQuantIndicator, saveQuantIndicator } from '@/app/hooks/useQuantIndicators'
import type { QuantIndicatorCatalogState } from '@/app/hooks/useQuantIndicatorCatalog'
import type { ManualIndicator } from '@/app/lib/quant/quantApi'
import { indicatorName } from '@/app/lib/quant/indicatorName'
import { parseIndicatorParams, toRawParams, type RawParamValues } from '@/app/lib/quant/indicatorValidation'
import type { IndicatorParamDef, IndicatorType } from '@/app/lib/quant/types'
import { useSignalLog } from '@/app/lib/signalLog'
import { QUANT_INDICATOR_SWATCHES, QuantIndicatorModal, type QuantIndicatorField } from '@/ui/QuantIndicatorModal'

export type QuantIndicatorModalState = { mode: 'pick' } | { mode: 'edit'; id: string | null; type: string }

export interface QuantIndicatorModalHostProps {
  modal: QuantIndicatorModalState
  catalog: QuantIndicatorCatalogState & { retry: () => void }
  indicators: ManualIndicator[]
  /** The symbol on screen, for the "added to" terminal line. */
  symbol: string
  onModal: (next: QuantIndicatorModalState | null) => void
}

function fieldKind(def: IndicatorParamDef): QuantIndicatorField['kind'] {
  return def.type
}

function defaultsOf(defs: IndicatorParamDef[]): Record<string, IndicatorParamDef['default']> {
  return Object.fromEntries(defs.map((d) => [d.name, d.default]))
}

interface EditFormProps {
  def: IndicatorType
  existing: ManualIndicator | undefined
  indicators: ManualIndicator[]
  symbol: string
  onModal: (next: QuantIndicatorModalState | null) => void
}

/** The edit-mode form: owns the raw field values, name, colour and errors. Remounts per indicator/type. */
function EditForm({ def, existing, indicators, symbol, onModal }: EditFormProps) {
  const { emit } = useSignalLog()
  const initialParams = existing?.params ?? defaultsOf(def.params)
  const [raw, setRaw] = useState<RawParamValues>(() => toRawParams(initialParams))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [name, setName] = useState(existing?.name ?? indicatorName(def.type, initialParams))
  const [nameAuto, setNameAuto] = useState(existing === undefined || existing.name === indicatorName(def.type, initialParams))
  const [color, setColor] = useState(
    existing?.color ??
      (QUANT_INDICATOR_SWATCHES.find((c) => !indicators.some((i) => i.color === c)) ?? QUANT_INDICATOR_SWATCHES[0]),
  )

  function onField(field: string, next: string | boolean) {
    const nextRaw = { ...raw, [field]: next }
    setRaw(nextRaw)
    setErrors((e) => ({ ...e, [field]: '' }))
    if (nameAuto) {
      const parsed = parseIndicatorParams(def.params, nextRaw)
      if (Object.keys(parsed.errors).length === 0) setName(indicatorName(def.type, parsed.values))
    }
  }

  function onSave() {
    const parsed = parseIndicatorParams(def.params, raw)
    if (Object.keys(parsed.errors).length > 0) {
      setErrors(parsed.errors)
      return
    }
    const finalName = name.trim() || indicatorName(def.type, parsed.values)
    if (existing) {
      saveQuantIndicator(existing.id, { params: parsed.values, name: finalName, color })
      emit('save', `${finalName} updated - backend recomputing.`)
    } else {
      addQuantIndicator({ id: newQuantIndicatorId(), type: def.type, params: parsed.values, name: finalName, color })
      emit('save', `${finalName} added to ${symbol}.`)
    }
    onModal(null)
  }

  function onRemove() {
    if (!existing) return
    removeQuantIndicator(existing.id)
    emit('warn', `${existing.name} removed.`)
    onModal(null)
  }

  const fields: QuantIndicatorField[] = def.params.map((p) => ({
    name: p.name,
    label: p.label,
    kind: fieldKind(p),
    value: raw[p.name] ?? '',
    options: p.options,
    error: errors[p.name] || undefined,
  }))

  return (
    <QuantIndicatorModal
      mode="edit"
      title={existing ? 'Indicator settings' : `New ${def.label}`}
      onClose={() => onModal(null)}
      typeLine={`${def.label} · ${def.kind}`}
      source={def.group.startsWith('From') ? 'Served over ws' : 'Built-in'}
      name={name}
      onName={(v) => {
        setName(v)
        setNameAuto(false)
      }}
      fields={fields}
      onField={onField}
      color={color}
      onColor={setColor}
      existing={existing !== undefined}
      onRemove={onRemove}
      onBack={() => onModal({ mode: 'pick' })}
      onSave={onSave}
      saveLabel={existing ? 'Save & recompute' : 'Add to chart'}
    />
  )
}

/**
 * Container for the indicator modal (spec 036, handoff 4.5): pick mode lists
 * the catalog, edit mode edits one indicator. Writes go to the in-memory
 * indicator store; the market hook turns the change into a diff-only
 * `sendIndicators`, so no candles restream. Esc and backdrop discard.
 */
export function QuantIndicatorModalHost({ modal, catalog, indicators, symbol, onModal }: QuantIndicatorModalHostProps) {
  const close = () => onModal(null)
  if (catalog.status !== 'ready') {
    return (
      <QuantIndicatorModal
        mode="pick"
        title={modal.mode === 'pick' ? 'Add indicator' : 'Indicator settings'}
        onClose={close}
        placeholder={
          catalog.status === 'error' ? (
            <div className="flex flex-col items-start gap-2">
              <p role="alert" className="font-mono text-[11px] text-alert">
                {catalog.message}
              </p>
              <button
                type="button"
                onClick={catalog.retry}
                className="cursor-pointer border border-ink-25 px-3 py-1.5 font-sans text-[10px] font-semibold tracking-[0.18em] uppercase"
              >
                Retry
              </button>
            </div>
          ) : (
            <p role="status" className="font-mono text-[11px] text-ink-50">
              Loading indicators…
            </p>
          )
        }
      />
    )
  }

  if (modal.mode === 'pick') {
    const groups: Array<{ group: string; items: IndicatorType[] }> = []
    for (const t of catalog.types) {
      const g = groups.find((x) => x.group === t.group)
      if (g) g.items.push(t)
      else groups.push({ group: t.group, items: [t] })
    }
    return (
      <QuantIndicatorModal
        mode="pick"
        title="Add indicator"
        onClose={close}
        groups={groups.map((g) => ({
          group: g.group,
          items: g.items.map((t) => ({ type: t.type, label: t.label, kind: t.kind, description: t.description })),
        }))}
        onPick={(type) => onModal({ mode: 'edit', id: null, type })}
      />
    )
  }

  const existing = modal.id === null ? undefined : indicators.find((i) => i.id === modal.id)
  const def = catalog.types.find((t) => t.type === modal.type)
  if (def === undefined || (modal.id !== null && existing === undefined)) {
    return (
      <QuantIndicatorModal
        mode="pick"
        title="Indicator settings"
        onClose={close}
        placeholder={
          <p role="alert" className="font-mono text-[11px] text-alert">
            This indicator is no longer available.
          </p>
        }
      />
    )
  }
  return (
    <EditForm
      key={`${modal.id ?? 'new'}:${modal.type}`}
      def={def}
      existing={existing}
      indicators={indicators}
      symbol={symbol}
      onModal={onModal}
    />
  )
}
